import {useRef, useEffect, useCallback, useState, useMemo} from 'react';

import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import useRecenter from '~/shared/components/Maps/useAutoRecenter/useRecenter';
import {Coordinate, MapModeEnum} from '~/shared/components/Maps/types';
import {getInnerMapPadding} from '~/shared/components/Maps/utils';

const AUTO_RECENTER_INTERVAL = 5000;
const AUTO_RECENTER_INTERVAL_AFTER_INTERACTION = 30000;

const isFirstRecenterOnModeAllowed = (mode: MapModeEnum, isCourierLocationUsed: boolean) => (
  mode !== MapModeEnum.PICKED_UP &&
  mode !== MapModeEnum.DOOR_BELL
) || isCourierLocationUsed;

const useAutoRecenter = ({
  map,
  mode,
  isCourierLocationUsed,
  userLocation,
  businessLocation,
  courierLocation,
  onRecenterSuccessCallback,
  maxZoom,
}: {
  map: google.maps.Map | null;
  mode: MapModeEnum;
  isCourierLocationUsed: boolean;
  courierLocation?: Coordinate | null;
  userLocation?: Coordinate | null;
  businessLocation?: Coordinate | null;
  onRecenterSuccessCallback?: () => void;
  maxZoom?: number;
}) => {
  const autoRecenterIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resetCenterAfterInteractionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastInitialModeRecenter = useRef<MapModeEnum | null>(null);
  // if the user interacts with the map, we need to stop the auto recentering until the user stops interacting
  const [isUserInteractionIntervalRunning, setIsUserInteractionIntervalRunning] = useState<boolean>(false);
  const recenterPointRef = useRef<Coordinate | null | undefined>(null);

  const {
    isAutoRecenter,
    recenterMapHandler,
    optimalCenter,
    optimalZoom,
  } = useRecenter();

  const isMinLargeMobile = useIsMinLargeMobile();
  const innerMapPadding = useMemo(() => getInnerMapPadding(isMinLargeMobile), [isMinLargeMobile]);

  useEffect(() => {
    recenterPointRef.current = mode === MapModeEnum.PICKED_UP || mode === MapModeEnum.DOOR_BELL ?
      courierLocation || businessLocation :
      businessLocation;
  }, [courierLocation, businessLocation, mode]);
  
  const onRecenter = useCallback(() => {
    if (!map || !recenterPointRef.current || !userLocation) {
      return;
    }

    recenterMapHandler({map, innerMapPadding, userLocation, serviceCoordinate: recenterPointRef.current, maxZoom});
    setIsUserInteractionIntervalRunning(false);

    if (onRecenterSuccessCallback) {
      onRecenterSuccessCallback();
    }
  }, [map, userLocation, innerMapPadding, recenterMapHandler, onRecenterSuccessCallback, maxZoom]);

  const clearResetCenterAfterInteractionTimeoutRef = useCallback(() => {
    if (!resetCenterAfterInteractionTimeoutRef.current) {
      return;
    }
    clearTimeout(resetCenterAfterInteractionTimeoutRef.current);
    resetCenterAfterInteractionTimeoutRef.current = null;
  }, []);

  const onZoomOrCenterChange = useCallback(() => {
    if (resetCenterAfterInteractionTimeoutRef.current) {
      clearResetCenterAfterInteractionTimeoutRef();
    }

    if (isAutoRecenter()) {
      setIsUserInteractionIntervalRunning(false);
      return;
    }

    setIsUserInteractionIntervalRunning(true);
    resetCenterAfterInteractionTimeoutRef.current = setTimeout(() => {
      onRecenter();
      setIsUserInteractionIntervalRunning(false);
    }, AUTO_RECENTER_INTERVAL_AFTER_INTERACTION);
  }, [onRecenter, isAutoRecenter, clearResetCenterAfterInteractionTimeoutRef]);

  useEffect(() => {
    if (
      isFirstRecenterOnModeAllowed(mode, isCourierLocationUsed) &&
      lastInitialModeRecenter.current !== mode
    ) {
      onRecenter();
      lastInitialModeRecenter.current = mode;
    }
  }, [onRecenter, isCourierLocationUsed, mode]);

  useEffect(() => {
    if (isUserInteractionIntervalRunning) {
      return;
    }

    if (!autoRecenterIntervalRef.current) {
      autoRecenterIntervalRef.current = setInterval(() => {
        onRecenter();
      }, AUTO_RECENTER_INTERVAL);
    }

    return () => {
      if (autoRecenterIntervalRef.current) {
        clearInterval(autoRecenterIntervalRef.current);
        autoRecenterIntervalRef.current = null;
      }
    };
  }, [onRecenter, isUserInteractionIntervalRunning]);

  return {onRecenter, onZoomOrCenterChange, optimalZoom, optimalCenter};
};

export default useAutoRecenter;
